import app from 'flarum/forum/app';
import m from 'mithril';
import { extend } from 'flarum/common/extend';
import SessionDropdown from 'flarum/forum/components/SessionDropdown';
import Switch from 'flarum/common/components/Switch';
import LinkButton from 'flarum/common/components/LinkButton';
import { registerDashboardTab } from './dashboard';
import Monitor from './models/Monitor';
import Incident from './models/Incident';
import StatusPage from './models/StatusPage';
import Webhook from './models/Webhook';
import UptimeLanding from './components/UptimeLanding';
import ProModal from './components/ProModal';
import touchModals from './touchModals';
import * as lazy from './lazyPages';
app.initializers.add('linkrobins-lr-monitoring', () => {
  // The monthly uptime report (Pro): on unless switched off here or by the
  // link in the email. Only offered to people who can use monitoring.
  // SettingsPage is a lazy chunk in Flarum 2, so it is extended by module
  // path, as core's own extensions do.
  extend('flarum/forum/components/SettingsPage', 'notificationsItems', function (items) {
    if (!app.forum.attribute('lrCanUseMonitoring')) return;
    const user = this.user;
    const on = user.preferences()?.lrMonthlyReport !== false;
    items.add('lrMonthlyReport', m(Switch, {
      state: on,
      loading: this.lrReportSaving,
      onchange: value => {
        this.lrReportSaving = true;
        user.savePreferences({
          lrMonthlyReport: value
        }).then(() => {
          this.lrReportSaving = false;
          m.redraw();
        });
      }
    }, app.translator.trans('linkrobins-lr-monitoring.forum.monthly_report.setting')), 95);
  });
  touchModals();
  app.store.models['lr-monitors'] = Monitor;
  app.store.models['lr-incidents'] = Incident;
  app.store.models['lr-status-pages'] = StatusPage;
  app.store.models['lr-webhooks'] = Webhook;

  // The uptime section of lr-home's home page, handed over rather than
  // imported from there.
  //
  // It is handed over in this direction, and through `app` rather than a
  // module, for the same reason the dashboard tabs are a registry (./dashboard):
  // lr-home must not import out of this bundle. A cross-extension `ext:` import
  // is resolved when the importing bundle is evaluated, so if this extension is
  // disabled — or merely loads second — it yields undefined, and a home page
  // that renders an undefined component is a BLANK page, not a page missing a
  // section. That is exactly what shipped on 2026-09-27 and had to be reverted.
  //
  // Referencing it here is also what keeps it in this bundle at all. Nothing
  // else has referenced UptimeLanding since /uptime was merged into the home
  // page, and webpack drops what nothing references; the module registry only
  // holds what is in the graph. Delete this line and the section silently
  // vanishes from the build.
  app.lrUptimeLanding = UptimeLanding;

  // The Pro modal stays in the main bundle too. lr-home's index nudge opens it
  // through flarum.reg.get('linkrobins-lr-monitoring', 'components/ProModal')
  // when clicked, which only finds modules the main bundle registered; since
  // the dashboard pages became lazy chunks, nothing else here keeps it in.
  app.lrProModal = ProModal;

  // /dashboard IS the monitors tab; there is no summary page in front of it,
  // because a summary of a summary is not worth a click.
  //
  // The dashboard pages load on demand; see lazyPages.ts for what that takes.
  app.routes['lr.monitors'] = {
    path: '/dashboard',
    component: lazy.MonitorsPage
  };
  app.routes['lr.statusPages'] = {
    path: '/dashboard/status-pages',
    component: lazy.StatusPagesPage
  };
  app.routes['lr.monitor'] = {
    path: '/dashboard/monitors/:id',
    component: lazy.MonitorPage
  };
  app.routes['lr.export'] = {
    path: '/dashboard/export',
    component: lazy.ExportPage
  };
  // Link Robins Pro is a modal (ProModal). This address only exists because
  // Stripe returns here after checkout; it opens the modal on /dashboard.
  app.routes['lr.pro'] = {
    path: '/dashboard/pro',
    component: lazy.ProPage
  };
  registerDashboardTab({
    key: 'uptime',
    route: 'lr.monitors',
    label: 'linkrobins-lr-monitoring.forum.tabs.uptime',
    icon: 'fas fa-heart-pulse',
    priority: 10
  });
  registerDashboardTab({
    key: 'statusPages',
    route: 'lr.statusPages',
    label: 'linkrobins-lr-monitoring.forum.tabs.statusPages',
    icon: 'fas fa-square-poll-vertical',
    priority: 20
  });
  registerDashboardTab({
    key: 'export',
    route: 'lr.export',
    label: 'linkrobins-lr-monitoring.forum.export.nav',
    icon: 'fas fa-file-export',
    priority: 30
  });

  // The dashboard is reached from the account menu, next to the other things
  // that belong to the person rather than to the forum.
  //
  // Guarded because everything above runs in the same initializer: a component
  // that has moved between core versions is undefined here, and reaching for
  // .prototype on it throws before a single route is registered — which takes
  // the whole extension down over a menu item.
  try {
    extend(SessionDropdown.prototype, 'items', function (items) {
      if (!app.session.user) return;
      items.add('lr-dashboard', m(LinkButton, {
        icon: "fas fa-gauge-high",
        href: app.route('lr.monitors')
      }, app.translator.trans('linkrobins-lr-monitoring.forum.dashboard.nav')),
      // Above Settings and Log Out.
      90);
    });
  } catch (e) {
    console.error('[lr-monitoring] could not add the dashboard link to the account menu', e);
  }
});